import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { getStoredToken } from '@/lib/auth-storage';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/api-error';
import type { NotificacaoItem } from '@/types/notificacao';

type ListaRes = {
  items: NotificacaoItem[];
  total: number;
  page: number;
  pageSize: number;
};

export function NotificacoesBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const contagemQ = useQuery({
    queryKey: ['notificacoes', 'contagem'],
    queryFn: () => api.get<{ count: number }>('/notificacoes/nao-lidas/contagem').then((r) => r.data),
    refetchInterval: 45_000,
  });

  const listaQ = useQuery({
    queryKey: ['notificacoes', 'recent'],
    queryFn: () =>
      api.get<ListaRes>('/notificacoes', { params: { limit: 15, page: 1 } }).then((r) => r.data),
    enabled: open,
  });

  // SSE — escuta eventos em tempo real do backend
  useEffect(() => {
    const token = getStoredToken();
    if (!token) return;

    const baseUrl = (import.meta.env.VITE_API_URL?.trim() ?? '').replace(/\/$/, '');
    const apiBase = baseUrl.endsWith('/api/v1') ? baseUrl : baseUrl ? `${baseUrl}/api/v1` : '/api/v1';
    const sseUrl = `${apiBase}/notificacoes/stream?token=${encodeURIComponent(token)}`;

    let es: EventSource;
    let reconnectTimer: ReturnType<typeof setTimeout>;
    let destroyed = false;

    function connect() {
      es = new EventSource(sseUrl);

      es.onmessage = (e: MessageEvent<string>) => {
        try {
          const data = JSON.parse(e.data) as { tipo: string; titulo?: string; osId?: string; resultado?: string };
          if (data.tipo === 'heartbeat' || data.tipo === 'connected') return;
          if (data.tipo === 'nova_notificacao') {
            // Atualiza badge e lista imediatamente
            void qc.invalidateQueries({ queryKey: ['notificacoes'] });

            // Toast com link para a OS
            const icon = data.resultado === 'APROVADA' ? '✅' : data.resultado === 'REJEITADA' ? '❌' : '⚠️';
            toast(data.titulo ?? 'Nova resposta de cliente', {
              description: 'Clique para abrir a OS',
              icon,
              duration: 8000,
              action: data.osId
                ? { label: 'Abrir OS', onClick: () => navigate(`/os/${data.osId}`) }
                : undefined,
            });
          }
        } catch { /* json inválido */ }
      };

      es.onerror = () => {
        es.close();
        if (!destroyed) {
          reconnectTimer = setTimeout(connect, 5_000);
        }
      };
    }

    connect();

    return () => {
      destroyed = true;
      clearTimeout(reconnectTimer);
      es?.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const marcarLida = useMutation({
    mutationFn: (id: string) => api.patch(`/notificacoes/${id}/lida`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notificacoes'] });
    },
  });

  const marcarTodas = useMutation({
    mutationFn: () => api.post('/notificacoes/marcar-todas-lidas'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notificacoes'] });
    },
  });

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const count = contagemQ.data?.count ?? 0;
  const items = listaQ.data?.items ?? [];

  const handleOpenItem = async (n: NotificacaoItem) => {
    if (!n.lida) {
      try {
        await marcarLida.mutateAsync(n.id);
      } catch {
        /* navega mesmo se falhar */
      }
    }
    setOpen(false);
    if (n.link?.startsWith('/')) {
      navigate(n.link);
    }
  };

  return (
    <div className="relative" ref={rootRef}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="relative text-muted-foreground"
        aria-label={count > 0 ? `Notificações (${count} não lidas)` : 'Notificações'}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((o) => !o)}
      >
        <Bell className="h-6 w-6" strokeWidth={2} aria-hidden />
        {count > 0 ? (
          <Badge
            variant="destructive"
            className="absolute -right-0.5 -top-0.5 h-5 min-w-5 px-1 text-[10px] leading-none"
          >
            {count > 99 ? '99+' : count}
          </Badge>
        ) : null}
      </Button>

      {open ? (
        <div
          className="absolute right-0 top-full z-50 mt-2 flex w-[min(calc(100vw-2rem),22rem)] flex-col rounded-lg border border-border bg-card shadow-lg"
          role="dialog"
          aria-label="Lista de notificações"
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
            <span className="text-sm font-semibold">Notificações</span>
            <div className="flex items-center gap-1">
              {count > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  disabled={marcarTodas.isPending}
                  onClick={() => marcarTodas.mutate()}
                >
                  {marcarTodas.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Todas lidas
                </Button>
              ) : null}
              <Button variant="link" size="sm" className="h-8 px-1 text-xs" asChild>
                <Link to="/notificacoes" onClick={() => setOpen(false)}>
                  Ver todas
                </Link>
              </Button>
            </div>
          </div>

          <div className="max-h-[min(60vh,24rem)] overflow-y-auto">
            {listaQ.isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
              </div>
            ) : listaQ.isError ? (
              <p className="px-3 py-4 text-sm text-destructive">{getApiErrorMessage(listaQ.error)}</p>
            ) : items.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">Sem notificações.</p>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      className={cn(
                        'w-full px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/80',
                        !n.lida && 'bg-muted/40',
                      )}
                      onClick={() => void handleOpenItem(n)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className={cn('font-medium leading-snug', !n.lida && 'text-foreground')}>
                          {n.titulo}
                        </span>
                        {!n.lida ? (
                          <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden />
                        ) : null}
                      </div>
                      {n.corpo ? (
                        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{n.corpo}</p>
                      ) : null}
                      <p className="mt-1 text-[11px] text-muted-foreground tabular-nums">
                        {new Date(n.createdAt).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
